import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  CalendarIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import DataTable, { DataTableColumn, dataTableRowClass, dataTableTdClass } from '../../ui/DataTable';
import StatCard from '../../ui/StatCard';
import { cn } from '../../../DashboardPage/lib/utils';
import { tokenApi } from '../../../repositories/token';
import {columnMapApi} from '../../../repositories/columnMap'
import { Link } from 'react-router-dom';
import CreateTokenModal from '../../../components/modal/CreateTokenModal';
import UpdateTokenModal from '../../../components/modal/UpdateTokenModal';
import IntegrationModal from '../../../components/modal/IntegrationModal';
import { set } from 'date-fns';
import { Skeleton, SkeletonStatCards, SkeletonTableRows } from '../../ui/skeleton';
import SettingsPanel from '../../ui/SettingsPanel';
import RowActionsMenu, { RowMenuItem } from '../../ui/RowActionsMenu';
import Button from '../../ui/Button';
import { swalColors } from '../../../utils/swal';

interface ApiToken {
  id: string;
  name: string;
  status: boolean;
  scopes: string;
  allowed_domains: string[];
  expires_at: string;
  createdAt: string;
  token?: string;
}

interface CreateTokenForm {
  name: string;
  domains: string;
  expirationDate: string;
  readPermission: boolean;
  writePermission: boolean;
}

interface UpdateTokenForm {
  name: string;
  domains: string;
  expirationDate: string;
  readPermission: boolean;
  writePermission: boolean;
  status: boolean;
}
const isValidDomainWithWildcard = (domain: string): boolean => {
  const trimmedDomain = domain.trim();
  // Allow only "*" as special case
  if (trimmedDomain === '*') {
    return true;
  }
  // Normal domain regex (no wildcard allowed here)
  const domainRegex = /^([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(trimmedDomain);
};

const Token: React.FC = () => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenModal, setshowTokenModal] = useState(false);
  const [showExpirationDateInput, setShowExpirationDateInput] = useState(false);
  const [showToken, setShowToken] = useState<string | null>(null);
  // Token the integration modal is mapping columns for.
  const [integrationTokenId, setIntegrationTokenId] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState("");

  // Update token information
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUpdateTokenInformation, setSelectedUpdateTokenInfomation] = useState<ApiToken | null>(null);

  // Domain name in separated form for create
  const [domainTags, setDomainTags] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');

  // Domain name in separated form for update
  const [updateDomainTags, setUpdateDomainTags] = useState<string[]>([]);
  const [updateDomainInput, setUpdateDomainInput] = useState('');
  const [updateDomainError, setUpdateDomainError] = useState('');

  // Editing domain state
  const [editingDomainIdx, setEditingDomainIdx] = useState<number | null>(null);
  const [editingDomainValue, setEditingDomainValue] = useState('');

  const [updateForm, setUpdateForm] = useState<UpdateTokenForm>({
    name: '',
    domains: '',
    expirationDate: '',
    readPermission: false,
    writePermission: false,
    status: true,
  });

  const [createForm, setCreateForm] = useState<CreateTokenForm>({
    name: '',
    domains: '',
    expirationDate: '',
    readPermission: false,
    writePermission: false,
  });
  const handleMappedData = async (mappedData: any): Promise<boolean> => {
    const payload = {
      tokenId: integrationTokenId,
      platform: integrationType,
      mapping: mappedData
    };

    try {
      // 1. Check if mapping already exists
      const checkRes = await columnMapApi.check(integrationTokenId!, integrationType);
      const exist = checkRes.data.exist;

      if (exist) {
        // 2. Ask user if they want to replace
        const result = await Swal.fire({
          title: `${integrationType} Mapping Already Exists`,
          text: `You have already integrated ${integrationType} for this token. Do you want to replace it?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Keep Existing"
        });

        if (result.isConfirmed) {
          // 3. Replace (update) mapping
          try {
            await columnMapApi.update(checkRes.data?.data?.id, payload);
            Swal.fire({
              title: "Replaced!",
              text: "Integration mapping has been replaced.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
            setShowIntegrationModal(false);
            return true;
          } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Failed to update mapping.";
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
            });
            return false;
          }
        } else {
          Swal.fire({
            title: "Kept Existing",
            text: "Your previous integration mapping was kept.",
            icon: "info",
            timer: 2000,
            showConfirmButton: false,
          });
          setShowIntegrationModal(false);
          return true; // User chose to keep existing, treat as success
        }
      } else {
        // 4. Save new mapping
        try {
          await columnMapApi.save(payload);
          Swal.fire({
            title: "Saved!",
            text: "Integration mapping has been saved.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          setShowIntegrationModal(false);
          return true;
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message || "Failed to save mapping.";
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
          });
          return false;
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong.";
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });
      return false;
    }
  }

  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationType, setIntegrationType] = useState<"Airtable" | "Google Sheet">("Airtable");

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const res = await tokenApi.getAll();
      setTokens(res.data.data);
    } catch (error) {
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteTokens = async (id: string) => {
    setLoading(true);
    try {
      await tokenApi.delete(id);
    } catch (error) {
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);



  // Open update modal and fill form
  const handleOpenUpdateModal = (token: any) => {
    setSelectedUpdateTokenInfomation(token);
    setUpdateForm({
      name: token.name || '',
      domains: Array.isArray(token.allowed_domains)
        ? token.allowed_domains.join(', ')
        : token.allowed_domains || '',
      expirationDate: token.expires_at ? token.expires_at.split('T')[0] : '',
      readPermission: token.scopes?.includes('read') || false,
      writePermission: token.scopes?.includes('write') || false,
      status: token.status,
    });
    setShowExpirationDateInput(!!token.expires_at);
    setUpdateDomainTags(
      Array.isArray(token.allowed_domains)
        ? token.allowed_domains
        : (token.allowed_domains || '').split(',').map((d: string) => d.trim()).filter(Boolean)
    );
    setUpdateDomainInput('');
    setUpdateDomainError('');
    setShowUpdateModal(true);
  };

  const handleUpdateFormChange = (field: keyof UpdateTokenForm, value: any) => {
    setUpdateForm({ ...updateForm, [field]: value });
  };

  // Update token handler (does NOT update token value)
  const handleUpdateToken = async () => {
    if (!selectedUpdateTokenInformation) return;

    if (!updateForm.name.trim()) {
      Swal.fire({
        title: 'Error!',
        text: 'Token name is required.',
        icon: 'error',
      });
      return;
    }

    if (updateDomainTags.length === 0) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one domain is required.',
        icon: 'error',
      });
      return;
    }
    const invalidDomains = updateDomainTags.filter(domain => !isValidDomainWithWildcard(domain));
    if (invalidDomains.length > 0) {
      Swal.fire({
        title: 'Error!',
        text: `Invalid domain format: ${invalidDomains.join(', ')}. Please use valid domain names (e.g., example.com, *.example.com)`,
        icon: 'error',
      });
      return;
    }

    if (!updateForm.readPermission && !updateForm.writePermission) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one permission must be selected.',
        icon: 'error',
      });
      return;
    }

    const payload = {
      name: updateForm.name,
      allowed_domains: updateDomainTags.join(','),
      expires_at: showExpirationDateInput && updateForm.expirationDate
        ? updateForm.expirationDate
        : null,
      scopes: [
        ...(updateForm.readPermission ? ['read'] : []),
        ...(updateForm.writePermission ? ['write'] : []),
      ].join(','),
      status: updateForm.status
    };

    try {
      await tokenApi.update(selectedUpdateTokenInformation.id, payload);
      setShowUpdateModal(false);
      Swal.fire({
        title: 'Updated!',
        text: 'API token has been updated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchTokens();
    } catch (error: any) {
      const responseData = error?.response?.data;
      let errorMessage = "Something went wrong.";

      if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
        errorMessage = responseData.errors.join('\n');
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
      });
    }
  };

  // Helper functions
  const getActiveTokens = () => tokens.filter(token => token.status).length;
  const getExpiredTokens = () => {
    const today = new Date();
    return (tokens || []).filter(token =>
      token.expires_at && new Date(token.expires_at) < today
    ).length;
  };
  const getTotalTokens = () => tokens.length;

  const formatDateTime = (isoString: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US');
  };

  const handleCreateToken = () => {
    // Reset all create form state
    setCreateForm({
      name: '',
      domains: '',
      expirationDate: '',
      readPermission: false,
      writePermission: false,
    });
    setDomainTags([]);
    setDomainInput('');
    setDomainError('');
    setShowExpirationDateInput(false);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    // Reset create form state
    setCreateForm({
      name: '',
      domains: '',
      expirationDate: '',
      readPermission: false,
      writePermission: false,
    });
    setDomainTags([]);
    setDomainInput('');
    setDomainError('');
  };

  const handleshowTokenModal = () => {
    setshowTokenModal(true);
  };

  const handlecloseTokenModal = () => {
    setshowTokenModal(false);
  };

  const handleFormChange = (field: keyof CreateTokenForm, value: string | boolean) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitToken = async () => {
    if (!createForm.name.trim()) {
      Swal.fire({
        title: 'Error!',
        text: 'Token name is required.',
        icon: 'error',
      });
      return;
    }

    if (domainTags.length === 0) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one domain is required.',
        icon: 'error',
      });
      return;
    }
    const invalidDomains = domainTags.filter(domain => !isValidDomainWithWildcard(domain));
    if (invalidDomains.length > 0) {
      Swal.fire({
        title: 'Error!',
        text: `Invalid domain format: ${invalidDomains.join(', ')}. Please use valid domain names (e.g., example.com, *.example.com)`,
        icon: 'error',
      });
      return;
    }

    if (!createForm.readPermission && !createForm.writePermission) {
      Swal.fire({
        title: 'Error!',
        text: 'At least one permission must be selected.',
        icon: 'error',
      });
      return;
    }

    const payload = {
      name: createForm.name,
      allowed_domains: domainTags.join(','),
      scopes: [
        ...(createForm.readPermission ? ['read'] : []),
        ...(createForm.writePermission ? ['write'] : [])
      ].join(','),
      expires_at: showExpirationDateInput && createForm.expirationDate
        ? createForm.expirationDate
        : null,
    };

    try {
      const res = await tokenApi.create(payload);
      setCreatedToken(res.data.data.token);
      setDomainTags([]);
      setDomainInput('');
      handleshowTokenModal();
      handleCloseModal();
      await fetchTokens();
    } catch (error: any) {
      const responseData = error?.response?.data;
      let errorMessage = "Something went wrong.";

      if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
        errorMessage = responseData.errors.join('\n');
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
      });
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action will delete the API token permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, Delete it!',
    });

    if (result.isConfirmed) {
      try {
        await deleteTokens(tokenId);
        fetchTokens();
        Swal.fire({
          title: 'Deleted!',
          text: 'API token has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete token.',
          icon: 'error',
        });
      }
    }
  };

  const toggleTokenVisibility = (tokenId: string) => {
    setShowToken(showToken === tokenId ? null : tokenId);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      title: 'Copied!',
      text: 'Token copied to clipboard.',
      icon: 'success',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    });
  };

  if (loading) {
    return (
      <SettingsPanel loading className="w-full max-w-full">
        <Skeleton className="w-56 h-7 mb-2" />
        <Skeleton className="w-72 h-4 mb-6" />
        <SkeletonStatCards count={3} className="mb-8" />
        <Skeleton className="w-32 h-5 mb-3" />
        <div className="overflow-hidden border rounded-lg border-neutral-200">
          <table className="min-w-full">
            <tbody>
              <SkeletonTableRows rows={4} columns={7} />
            </tbody>
          </table>
        </div>
      </SettingsPanel>
    );
  }

  // Header only — the rows are composed below so each keeps its own actions menu.
  const tokenColumns: DataTableColumn<any>[] = [
    { key: "name", header: "Name" },
    { key: "status", header: "Status" },
    { key: "scopes", header: "Scopes" },
    { key: "domains", header: "Domains" },
    { key: "created", header: "Created" },
    { key: "expires", header: "Expires" },
    { key: "actions", header: "Actions" },
  ];

  return (
    <SettingsPanel
      className="w-full overflow-x-hidden"
      title="API Token Management"
      description="Manage your API tokens and access permissions."
      actions={
        <Button
          onClick={handleCreateToken}
          className="w-full sm:w-auto"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Token
        </Button>
      }
    >

      {/*
        Token Summary Cards. These are one of the few places where a per-card
        icon colour is genuinely meaningful (active vs expired), so the badge
        tone is overridden rather than left uniform.
      */}
      <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-3">
        <StatCard
          label="Active Tokens"
          value={getActiveTokens()}
          icon={<CheckCircleIcon className="w-5 h-5" />}
          iconClassName="bg-success-50 text-success-700"
        />
        <StatCard
          label="Expired Tokens"
          value={getExpiredTokens()}
          icon={<XCircleIcon className="w-5 h-5" />}
          iconClassName="bg-danger-50 text-danger-600"
        />
        <StatCard
          label="Total Tokens"
          value={getTotalTokens()}
          icon={<KeyIcon className="w-5 h-5" />}
        />
      </div>
      {/* API Token Table */}
      {tokens.length > 0 ? (
        <div>
          <h3 className="mb-3 text-lg font-semibold text-black">API Tokens</h3>

          <DataTable columns={tokenColumns}>
                {tokens.map((token:any) => (
                 
                  
                  <tr key={token.id} className={cn(dataTableRowClass, "h-16")}>
                    <td className={dataTableTdClass}>
                      <div className="flex items-center">
                        <KeyIcon className="w-5 h-5 mr-2 text-neutral-400 sm:mr-3" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-neutral-900 truncate">
                            {token.name}
                          </div>
                          <div className="text-xs text-neutral-500 truncate sm:text-sm">
                            {token.id}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={dataTableTdClass}>
                      <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-medium border rounded-full ${token.status === true
                        ? 'bg-success-50 text-success-800 border border-success-200'
                        : 'bg-danger-50 text-danger-700 border border-danger-200'
                        }`}>
                        {token.status === true ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={dataTableTdClass}>
                      <div className="flex flex-wrap gap-1">
                        {token.scopes.split(',').map((scope:any, index:number) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full text-neutral-700 bg-neutral-100 border-neutral-200"
                          >
                            {scope.trim()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className={dataTableTdClass}>
                      <div className="max-w-[150px] sm:max-w-xs text-xs sm:text-sm text-neutral-900 truncate">
                        {Array.isArray(token.allowed_domains)
                          ? token.allowed_domains.join(', ')
                          : token.allowed_domains || 'No domains'}
                      </div>
                    </td>
                    <td className={dataTableTdClass}>
                      <div className="text-xs text-neutral-900 sm:text-sm">{formatDateTime(token.createdAt)}</div>
                    </td>
                    <td className={dataTableTdClass}>
                      <div className="text-xs text-neutral-900 sm:text-sm">
                        {token.expires_at
                          ? new Date(token.expires_at) < new Date()
                            ? "Expired"
                            : formatDateTime(token.expires_at)
                          : "-----"}
                      </div>
                    </td>
                    <td className={dataTableTdClass}>
                      <div className="flex items-center space-x-2">
                        {token.status === true && token.token && (
                          <>
                            <button
                              onClick={() => toggleTokenVisibility(token.id)}
                              className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
                              title={showToken === token.id ? 'Hide token' : 'Show token'}
                            >
                              {showToken === token.id ? (
                                <EyeSlashIcon className="w-4 h-4" />
                              ) : (
                                <EyeIcon className="w-4 h-4" />
                              )}
                            </button>
                            {showToken === token.id && (
                              <button
                                onClick={() => copyToClipboard(token.token!)}
                                className="text-xs text-primary-700 hover:text-primary-800"
                              >
                                Copy
                              </button>
                            )}
                          </>
                        )}
                        {/* Anchored menu is portaled out of DataTable's overflow container, so it is never clipped. */}
                        <RowActionsMenu label="Token actions" panelClassName="w-max min-w-[12rem]">
                          {token.allowed_domains.split(",").includes("airtable.com") && (
                            <RowMenuItem
                              tone="accent"
                              onClick={() => {
                                setIntegrationType("Airtable");
                                setShowIntegrationModal(true);
                                setIntegrationTokenId(token.id);
                              }}
                            >
                              Connect with Airtable
                            </RowMenuItem>
                          )}
                          {token.allowed_domains.split(",").includes("script.google.com") && (
                            <RowMenuItem
                              tone="accent"
                              onClick={() => {
                                setIntegrationType("Google Sheet");
                                setShowIntegrationModal(true);
                                setIntegrationTokenId(token.id);
                              }}
                            >
                              Connect with Google Sheet
                            </RowMenuItem>
                          )}
                          <RowMenuItem onClick={() => handleOpenUpdateModal(token)}>
                            Update
                          </RowMenuItem>
                          <RowMenuItem tone="danger" onClick={() => handleDeleteToken(token.id)}>
                            Delete
                          </RowMenuItem>
                        </RowActionsMenu>
                      </div>
                    </td>
                  </tr>
                ))}
          </DataTable>
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white border rounded-lg border-neutral-200">
          <div className="px-6 py-12 text-center">
            <div className="flex items-center justify-center w-24 h-24 mx-auto mb-6 bg-neutral-100 rounded-full">
              <KeyIcon className="w-12 h-12 text-neutral-400" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-neutral-900">No API tokens yet</h3>
            <p className="max-w-md mx-auto mb-6 text-neutral-500">
              Create your first API token to start integrating with our services.
              API tokens allow secure access to our APIs and services.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                onClick={handleCreateToken}
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Your First Token
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Token Modal */}
      <CreateTokenModal
        showModal={showCreateModal}
        onClose={handleCloseModal}
        createForm={createForm}
        onFormChange={handleFormChange}
        onSubmit={handleSubmitToken}
        showExpirationDateInput={showExpirationDateInput}
        setShowExpirationDateInput={setShowExpirationDateInput}
        domainTags={domainTags}
        setDomainTags={setDomainTags}
        domainInput={domainInput}
        setDomainInput={setDomainInput}
        domainError={domainError}
        setDomainError={setDomainError}
      />

      {/* Update Token Modal */}
      <UpdateTokenModal
        showModal={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        updateForm={updateForm}
        onFormChange={handleUpdateFormChange}
        onSubmit={handleUpdateToken}
        showExpirationDateInput={showExpirationDateInput}
        setShowExpirationDateInput={setShowExpirationDateInput}
        updateDomainTags={updateDomainTags}
        setUpdateDomainTags={setUpdateDomainTags}
        updateDomainInput={updateDomainInput}
        setUpdateDomainInput={setUpdateDomainInput}
        updateDomainError={updateDomainError}
        setUpdateDomainError={setUpdateDomainError}
        editingDomainIdx={editingDomainIdx}
        setEditingDomainIdx={setEditingDomainIdx}
        editingDomainValue={editingDomainValue}
        setEditingDomainValue={setEditingDomainValue}
      />

      {/* Show Token Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg">
            <div className="mb-6 text-center">
              <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 text-success-500" />
              <h2 className="text-xl font-semibold text-black">Token Created Successfully!</h2>
              <p className="mt-1 text-sm text-neutral-600">
                Please save this token securely. <span className="font-semibold text-warning-700">This is the only time it will be displayed.</span>
              </p>
            </div>

            {/* Token Box */}
            <div className="mb-4">
              <div className='flex items-center justify-between'>
                <p className='text-sm font-semibold'>Your API Token:</p>
                <button
                  onClick={() => copyToClipboard(createdToken)}
                  className="flex items-center gap-1 px-2 py-2 text-sm text-primary-700 hover:text-primary-700 "
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />copy
                </button>
              </div>
              <div className="relative p-3 overflow-y-auto font-mono text-xs text-success-700 break-words bg-neutral-100 border border-neutral-300 rounded-md max-h-40">
                {createdToken}
              </div>
            </div>

            {/* Security Notice */}
            <div className="p-4 mb-6 text-sm text-warning-800 bg-warning-50 border border-warning-200 rounded-md">
              <strong className="block mb-1 font-medium">Important Security Notice</strong>
              Make sure to copy and save this token in a secure location. Once you close this dialog, the token will not be displayed again.
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => copyToClipboard(createdToken)}
              >
                <ClipboardDocumentIcon className="w-5 h-5" />
                Copy Token
              </Button>
              <Button
                variant="neutral"
                onClick={handlecloseTokenModal}
              >
                I've Saved It
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Integration Modal */}
      <IntegrationModal
        isOpen={showIntegrationModal}
        onClose={handleMappedData}
        integrationType={integrationType}
        closeModal={setShowIntegrationModal}
      />
    </SettingsPanel>
  );
};

export default Token;